import React, { FC, MouseEvent } from 'react'
import { TIconComponent } from 'transfix-icons/types'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { IconWrapper } from 'transfix-ui'

const useSupportItemStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
    '&:active': {
      textDecoration: 'none',
      backgroundColor: brandColors.coolGray1,
    },
  },
  icon: {
    color: brandColors.coolGray6,
    paddingTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
  },
  textsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(2),
  },
  title: {
    color: brandColors.skyBlue6,
  },
  description: {
    color: brandColors.coolGray6,
  },
}))

interface ISupportItemProps {
  /**
   * Icon of the support item.
   */
  icon: TIconComponent
  /**
   * Title of the support item. (Primary text)
   */
  title: string
  /**
   * Description of the support item. (Secondary text)
   */
  description?: string
  /**
   * Where to navigate when the support item is clicked.
   */
  href?: string
  /**
   * Should the support item open in a new window/tab?
   */
  target?: 'blank'
  /**
   * A programmatic action to happen when the support item is clicked. (Doesn't
   *  prevent the default behavior).
   */
  onClick?: (clickEvent?: MouseEvent<HTMLElement>) => void
}

export const SupportItem: FC<ISupportItemProps> = ({
  description,
  href,
  icon,
  onClick,
  target,
  title,
}) => {
  const { isMobile } = useBreakpointContext()
  const classes = useSupportItemStyles()
  const handleClick = (clickEvent: MouseEvent<HTMLElement>) => {
    if (!href) {
      clickEvent.preventDefault()
    }
    if (onClick) {
      onClick(clickEvent)
    }
  }

  return (
    <a href={href} className={classes.wrapper} target={target} onClick={handleClick}>
      {/* Icon */}
      <div>
        <IconWrapper className={classes.icon} Icon={icon} size='xlarge' />
      </div>

      <div className={classes.textsWrapper}>
        {/* Title (Primary text) */}
        <Typography variant={isMobile ? 'body1' : 'body2'} className={classes.title}>
          {title}
        </Typography>

        {/* Description (Secondary text) */}
        <Typography variant={isMobile ? 'caption' : 'body2'} className={classes.description}>
          {description}
        </Typography>
      </div>
    </a>
  )
}
